import React, { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { listarContatos, type CrmContact } from '@/services/crmContacts';
import {
  LayoutDashboard,
  BookOpen,
  FolderTree,
  PanelsTopLeft,
  Image as ImageIcon,
  Library,
  Users,
  LogOut,
} from 'lucide-react';

export interface AdminSection {
  id: string;
  label: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}

const ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  dashboard: LayoutDashboard,
  tutorials: BookOpen,
  categories: FolderTree,
  'header-menus': PanelsTopLeft,
  media: ImageIcon,
  'knowledge-base': Library,
};

interface AdminLayoutProps {
  sections: AdminSection[];
  activeSection: string;
  onSelectSection: (id: string) => void;
  children: ReactNode;
}

/** Contatos (WhatsApp) do CRM, como aproximação de "dados de clientes" disponível hoje —
 *  não existe rota do CRM que liste clientes cadastrados no painel (Cadastro.Cliente). */
function ContatosWidget() {
  const { crmToken } = useAuth();
  const [contatos, setContatos] = useState<CrmContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!crmToken) {
      setLoading(false);
      return;
    }
    let cancelado = false;
    listarContatos(crmToken)
      .then((lista) => {
        if (!cancelado) setContatos(lista.slice(0, 15));
      })
      .catch((err) => {
        if (!cancelado) setErro(err instanceof Error ? err.message : 'Erro ao carregar');
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [crmToken]);

  return (
    <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #1e293b' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 0.75rem', marginBottom: '0.5rem' }}>
        <Users size={13} className="text-slate-400" />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', textTransform: 'uppercase' }}>
          Contatos (WhatsApp)
        </span>
      </div>

      {loading && <p style={{ fontSize: 12, color: '#64748b', padding: '0 0.75rem' }}>Carregando...</p>}
      {erro && <p style={{ fontSize: 12, color: '#f87171', padding: '0 0.75rem' }}>{erro}</p>}
      {!loading && !erro && contatos.length === 0 && (
        <p style={{ fontSize: 12, color: '#64748b', padding: '0 0.75rem' }}>Nenhum contato.</p>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 260, overflowY: 'auto' }}>
        {contatos.map((c) => (
          <li
            key={c.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0.4rem 0.75rem',
              borderRadius: 8,
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: '9999px',
                background: '#334155',
                color: '#cbd5e1',
                display: 'grid',
                placeItems: 'center',
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {(c.name || c.phone || '?').slice(0, 1).toUpperCase()}
            </span>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 12.5, color: '#e2e8f0', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.name || c.phone || 'Sem nome'}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminLayout({ sections, activeSection, onSelectSection, children }: AdminLayoutProps) {
  const { user, logout } = useAuth();

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Navbar */}
      <header
        style={{
          height: 60,
          background: '#0f172a',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1.5rem',
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: '#7c3aed', display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 13 }}>
            L
          </span>
          <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: '-0.01em' }}>Lukos · Painel Administrativo</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 13, color: '#cbd5e1' }}>{user?.name || user?.username}</span>
          <button
            type="button"
            onClick={() => logout()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(255,255,255,0.08)',
              border: 'none',
              color: '#e2e8f0',
              padding: '0.4rem 0.75rem',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <LogOut size={13} /> Sair
          </button>
        </div>
      </header>

      <div style={{ display: 'flex', flex: 1 }}>
        {/* Sidebar */}
        <aside style={{ width: 240, background: '#0f172a', color: '#fff', padding: '1.25rem 0', flexShrink: 0 }}>
          <nav>
            <ul style={{ listStyle: 'none', margin: 0, padding: '0 0.75rem', display: 'grid', gap: 2 }}>
              {sections.map((section) => {
                const Icon = section.icon ?? ICONS[section.id] ?? LayoutDashboard;
                const active = activeSection === section.id;
                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => onSelectSection(section.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        padding: '0.6rem 0.75rem',
                        borderRadius: 10,
                        border: 'none',
                        background: active ? '#7c3aed' : 'transparent',
                        color: active ? '#fff' : '#cbd5e1',
                        fontSize: 13.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <Icon size={16} />
                      {section.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <ContatosWidget />
        </aside>

        {/* Conteúdo */}
        <main style={{ flex: 1, minWidth: 0 }}>{children}</main>
      </div>
    </div>
  );
}
